/* eslint-disable max-len */
/* eslint-disable no-param-reassign */
import React, { useCallback, useContext, useState } from 'react';
import PropTypes from 'prop-types';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useTranslator } from '@jutro/locale';
import { MetadataContent } from '@jutro/legacy/uiconfig';
import { useModal } from '@jutro/components';
import { ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { FormModalPopover } from 'ml-components-platform-react';
import _ from 'lodash';
import '@xengage/gw-platform-translations';
import metadata from './MLHOEditScheduledPropertyComponent.metadata.json5';
import messages from './MLHOEditScheduledPropertyComponent.messages';
import styles from './MLHOEditScheduledPropertyComponent.module.scss';
import vppModalPopup from './MLHOValuablePersonalPropertyPopup.metadata.json5';
import sppModalPopup from './MLHOSchedulePersonalPropertyPopup.metadata.json5';
import appConfig from 'app-config';

const LOAD_MORE_INDEX = 5;

function MLHOEditScheduledPropertyComponent(props) {
    const modalApi = useModal();
    const {
        properties,
        defaultValue,
        dtoPath,
        isVpp,
        loading,
        onListChange,
        isAgent,
        isReadOnly
    } = props;

    const translator = useTranslator();
    const viewModelService = useContext(ViewModelServiceContext);

    const [loadMoreIndex, setLoadMoreIndex] = useState(LOAD_MORE_INDEX);

    const isQB = appConfig.portalCode === 'QB';

    const formatOptionAvailableVals = useCallback((options) => {
        if (!options) return [];
        return options.map((opt) => ({
            code: opt.code,
            name: {
                id: opt.code,
                defaultMessage: opt.name
            }
        }));
    }, []);

    const getResetDefaultVpp = (defaultVppObj) => {
        const copyOfVpp = _.cloneDeep(defaultVppObj);
        const keys = Object.keys(copyOfVpp);
        keys.forEach((k) => {
            // eslint-disable-next-line no-prototype-builtins
            if (copyOfVpp[k].hasOwnProperty('chosenTerm')) {
                copyOfVpp[k].chosenTerm = null;
                copyOfVpp[k].chosenTermValue = '';
            }
        });
        copyOfVpp.description = null;
        return _.omit(copyOfVpp, ['fixedId', 'coverageDescription', 'itemValue']);
    };

    const addItemHandler = useCallback((vm) => {
        if (vm.aspects.valid && vm.aspects.subtreeValid) {
            if (vm.value.itemValue) {
                vm.value.itemValue = parseFloat(vm.value.itemValue).toFixed(2);
            }
            properties.push(vm.value);
            onListChange(undefined, undefined, undefined, true);
            return true;
        }
        return false;
    }, [onListChange, properties]);

    const editItemHandler = useCallback((vm) => {
        const indexOfCurrentItem = properties.findIndex((e) => e.fixedId === vm.fixedId.value);
        if (vm.aspects.valid && vm.aspects.subtreeValid && indexOfCurrentItem !== -1) {
            if (vm.value.itemValue) {
                vm.value.itemValue = parseFloat(vm.value.itemValue).toFixed(2);
            }
            properties[indexOfCurrentItem] = vm.value;
            onListChange(undefined, undefined, undefined, true);
            return true;
        }
        return false;
    }, [onListChange, properties]);

    const getTitle = useCallback((isVpp, isEdit) => {
        let title = '';
        if(isVpp && !isEdit) {
            title = messages.modalAddVpp
        }else if(isVpp && isEdit) {
            title = messages.modalEditVpp
        }else if(!isVpp && !isEdit) {
            title = messages.modalAddSpp
        }else {
            title = messages.modalEditSpp
        }
        return title;
    })

    const getPopupProps = useCallback((vm, isEdit) => {
        const uiProps = isVpp ? vppModalPopup.pageContent : sppModalPopup.pageContent;
        return {
            title: getTitle(isVpp,isEdit),
            uiProps,
            actionBtnLabel: messages.modalContinue,
            cancelBtnLabel: isAgent ? messages.modalCancel : undefined,
            viewModel: vm,
            getOverrideProps: () => {
                return {
                    itemTypeDropdownSelect: {
                        value: vm.type.chosenTerm.value,
                        availableValues: formatOptionAvailableVals(vm.type.options.value),
                        validationMessages: vm.type.chosenTerm.aspects.validationMessages
                    },
                    // VPP
                    perItemTypeDropdownSelect: {
                        value: isVpp && vm.perItemLimit.chosenTerm.value,
                        availableValues: isVpp && formatOptionAvailableVals(vm.perItemLimit.options.value),
                        validationMessages: isVpp ? vm.perItemLimit.chosenTerm.aspects.validationMessages : undefined
                    },
                    totalLimitDropdownSelect: {
                        value: isVpp && vm.aggregateLimit.chosenTerm.value,
                        availableValues: isVpp && formatOptionAvailableVals(vm.aggregateLimit.options.value),
                        validationMessages: isVpp ? vm.aggregateLimit.chosenTerm.aspects.validationMessages : undefined
                    },

                    // SPP
                    deductibleDropdownSelect: {
                        value: !isVpp && vm.deductible.chosenTerm.value,
                        availableValues: !isVpp && formatOptionAvailableVals(vm.deductible.options.value),
                        validationMessages: !isVpp ? vm.deductible.chosenTerm.aspects.validationMessages : undefined
                    },
                    lossSettlementDropdownSelect: {
                        value: !isVpp && vm.lossSettlement.chosenTerm.value,
                        availableValues: !isVpp && formatOptionAvailableVals(vm.lossSettlement.options.value),
                        validationMessages: !isVpp ? vm.lossSettlement.chosenTerm.aspects.validationMessages : undefined
                    }
                };
            },
            onSaveAction: isEdit ? editItemHandler : addItemHandler
        };
    }, [addItemHandler, editItemHandler, formatOptionAvailableVals, isAgent, isVpp]);

    const onAddItem = useCallback(() => {
        const isEdit = false;
        const defaultVal = getResetDefaultVpp(defaultValue);
        const vm = viewModelService.create(defaultVal, 'pc', dtoPath);
        const componentProps = getPopupProps(vm, isEdit);

        modalApi.showModal(
            <FormModalPopover {...componentProps} />
        );
    }, [defaultValue, dtoPath, getPopupProps, viewModelService]);

    const onEditItem = useCallback((evt) => {
        const isEdit = true;
        const selectedItem = properties[evt.index];
        const selectedItemVm = viewModelService.create(selectedItem, 'pc', dtoPath);
        const componentProps = getPopupProps(selectedItemVm, isEdit);

        modalApi.showModal(
            <FormModalPopover {...componentProps} />
        );
    }, [dtoPath, getPopupProps, properties, viewModelService]);

    const onRemoveItem = useCallback((evt) => {
        properties.splice(evt.index, 1);
        onListChange(undefined, undefined, undefined, true);
    }, [onListChange, properties]);


    const onLoadMoreItems = () => {
        setLoadMoreIndex(loadMoreIndex + 5);
    };

    const onCollapseItems = () => {
        setLoadMoreIndex(5);
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            onEditItem,
            onRemoveItem,
            onLoadMoreItems,
            onCollapseItems,
            onAddItem
        }
    };

    const generateItemOverrides = useCallback(() => {
        const itemOverrides = properties.map((item, itemIndex) => {
            return {
                [`tableRowDiv${itemIndex}`]: {
                    visible: !isReadOnly || (isReadOnly && itemIndex < loadMoreIndex)
                },
                [`actionRow${itemIndex}`]: {
                    visible: !isReadOnly,
                    className: loading ? 'cell disabled' : 'cell'
                },
                // VPP
                [`perItemRowVpp${itemIndex}`]: {
                    visible: isVpp
                },
                [`totalLimitRowVpp${itemIndex}`]: {
                    visible: isVpp
                },
                // SPP
                [`deductibleRowSpp${itemIndex}`]: {
                    visible: !isVpp
                },
                [`lossSettlementRowSpp${itemIndex}`]: {
                    visible: !isVpp
                },
                [`valueRowSpp${itemIndex}`]: {
                    visible: !isVpp
                },
                [`descriptionRowSpp${itemIndex}`]: {
                    visible: !isVpp
                }
            };
        });

        return Object.assign({}, ...itemOverrides);
    }, [isReadOnly, isVpp, loadMoreIndex, loading, properties]);

    const getButtonContent = () => {
        if (isQB) {
            if (isVpp) {
                return translator(messages.addVpp);
            }
            return translator(messages.addSpp);
        }

        if (!isAgent) {
            return translator(messages.add);
        }
        if (isVpp) {
            return translator(messages.addVpp);
        }
        return translator(messages.addSpp);
    };

    const overrides = {
        parentDiv: {
            className: !isReadOnly ? 'ho-edit-scheduled-property' : ''
        },
        clearfixDiv: {
            className: isQB ? 'clearfix marginBottomRem' : 'clearfix'
        },
        stackTableDiv: {
            className: isQB ? 'stack-table plain marginBottomRem' : 'stack-table plain'
        },
        actionHeader: {
            visible: !isReadOnly
        },
        tableRowIterable: {
            data: properties
        },
        bottomButtonContainer: {
            visible: (isReadOnly && properties.length > 5) || !isReadOnly,
            className: isQB ? 'display-block margin-top-10 marginBottomRem' : 'display-block margin-top-10'
        },
        loadMoreItemBtn: {
            visible: isReadOnly && properties.length > loadMoreIndex,
            disabled: loading
        },
        collapseItemBtn: {
            visible: isReadOnly && loadMoreIndex > 5,
            disabled: loading
        },
        addItemBtn: {
            visible: !isReadOnly,
            content: getButtonContent()
        },
        // VPP
        perItemHeaderVpp: {
            visible: isVpp
        },

        totalLimitHeaderVpp: {
            visible: isVpp
        },
        // SPP
        deductibleHeaderSpp: {
            visible: !isVpp
        },
        lossSettlementHeaderSpp: {
            visible: !isVpp
        },
        valueHeaderSpp: {
            visible: !isVpp
        },
        descriptionHeaderSpp: {
            visible: !isVpp
        },
        ...generateItemOverrides()
    };

    return (
        <MetadataContent
            uiProps={metadata.componentContent}
            overrideProps={overrides}
            {...resolvers} />
    );
}

MLHOEditScheduledPropertyComponent.propTypes = {
    properties: PropTypes.arrayOf(PropTypes.shape({})),
    defaultValue: PropTypes.shape({}),
    dtoPath: PropTypes.string,
    isVpp: PropTypes.bool,
    onListChange: PropTypes.func,
    loading: PropTypes.bool,
    isAgent: PropTypes.bool,
    isReadOnly: PropTypes.bool
};

export default MLHOEditScheduledPropertyComponent;
